/**
 * TrustBadgeGroup Component
 *
 * Renders a responsive grid of trust badges (regulatory + security).
 * Pulls definitions from trustCopy constants.
 *
 * @example
 * ```tsx
 * <TrustBadgeGroup />
 * <TrustBadgeGroup variant="compact" />
 * ```
 */

import { cn } from "@/lib/utils";
import { TrustBadge } from "./TrustBadge";
import {
  REGULATORY_BADGES,
  SECURITY_BADGES,
  type TrustBadgeDefinition,
} from "@/constants/trustCopy";
import {
  Landmark,
  ShieldCheck,
  Lock,
  MapPin,
  ScanEye,
} from "lucide-react";
import type { ReactNode } from "react";

// ============================================================================
// Icon resolver
// ============================================================================

const ICON_MAP: Record<TrustBadgeDefinition["icon"], ReactNode> = {
  landmark: <Landmark className="h-4 w-4" />,
  "shield-check": <ShieldCheck className="h-4 w-4" />,
  lock: <Lock className="h-4 w-4" />,
  "map-pin": <MapPin className="h-4 w-4" />,
  "scan-eye": <ScanEye className="h-4 w-4" />,
};

// ============================================================================
// Component
// ============================================================================

export interface TrustBadgeGroupProps {
  /** "full" shows all badges with subtitles; "compact" shows a simpler row */
  variant?: "full" | "compact";
  className?: string;
}

export function TrustBadgeGroup({
  variant = "full",
  className,
}: TrustBadgeGroupProps) {
  const badges = [...REGULATORY_BADGES, ...SECURITY_BADGES];

  if (variant === "compact") {
    return (
      <div
        className={cn(
          "flex flex-wrap justify-center gap-3 text-sm text-muted-foreground",
          className
        )}
        role="list"
        aria-label="Trust and security indicators"
      >
        {badges.map((badge) => (
          <div
            key={badge.id}
            className="flex items-center gap-1.5"
            role="listitem"
          >
            <span className="text-primary">{ICON_MAP[badge.icon]}</span>
            <span>{badge.title}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3",
        className
      )}
      role="list"
      aria-label="Trust and security indicators"
    >
      {badges.map((badge) => (
        <div key={badge.id} role="listitem">
          <TrustBadge
            icon={ICON_MAP[badge.icon]}
            title={badge.title}
            subtitle={badge.subtitle}
            link={badge.link}
          />
        </div>
      ))}
    </div>
  );
}
